import React, {
  useState,
  useContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { EstimateContext } from "../Estimate";
import { useEstimateState } from "../EstimateContext";
import { calculateConstructionDesign } from "../../../../../api/projectEstimatesApi";
import "./LeadTab.css";

const defaultLeadRole = (roleName) => ({
  role: roleName,
  average_salary: 0,
  day_efforts: { direct: 0, bonus: 0 },
  money_expenditures: { direct: 0, bonus: 0 },
});

const LeadTab = forwardRef(({ isEditable = true }, ref) => {
  const estimateContext = useContext(EstimateContext);
  const {
    getSectionData,
    updateEstimateSection,
    estimateData,
    sectionDetails: contextSections,
    setEstimateData,
    performSave,
  } = useEstimateState();

  // Получаем данные ведения проекта из нового контекста состояния
  const projectDeliveryData = useMemo(
    () => getSectionData("project_delivery") || {},
    [getSectionData],
  );

  // Все разделы из API. Порядок по order_index (задаётся пользователем через drag-and-drop).
  const displayRoles = useMemo(() => {
    const fromApi = projectDeliveryData.roles || [];
    return [...fromApi].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
  }, [projectDeliveryData.roles]);

  // Разделы с вкладки «Рабочее проектирование» — только они доступны для добавления
  const workTabSections = useMemo(() => {
    const list = [];
    const seen = new Set();
    const add = (arr) => {
      (arr || []).forEach((item) => {
        const id = item?.section?.id;
        const name = item?.section?.name;
        if (id != null && name != null && !seen.has(id)) {
          seen.add(id);
          list.push({
            id,
            name,
            description: item?.section?.description ?? "",
          });
        }
      });
    };
    add(estimateData?.construction_design);
    add(estimateData?.construction_design_project);
    return list;
  }, [
    estimateData?.construction_design,
    estimateData?.construction_design_project,
  ]);

  const roles = displayRoles;
  // Используем данные с сервера для include_bonuses
  const includeBonuses = projectDeliveryData.include_bonuses ?? true;
  const qualityControlPercent = projectDeliveryData.quality_control_percent;
  const directBonusPercent = projectDeliveryData.direct_bonus_percent;

  // Состояния для редактируемых полей
  const [editingFields, setEditingFields] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [localValues, setLocalValues] = useState({
    include_bonuses: includeBonuses,
    quality_control_percent: qualityControlPercent,
    direct_bonus_percent: directBonusPercent,
    average_salary: projectDeliveryData.average_salary, // Общая средняя ставка
  });

  // Модалка добавления раздела: только разделы из Рабочего проектирования
  const [showAddLeadSectionModal, setShowAddLeadSectionModal] = useState(false);
  const [selectedLeadSections, setSelectedLeadSections] = useState([]);
  const [leadSectionSearch, setLeadSectionSearch] = useState("");

  // Обновляем локальные значения при изменении данных из контекста
  useEffect(() => {
    // Проверяем, есть ли активные поля в редактировании
    const hasActiveEditing = Object.values(editingFields).some(
      (isEditing) => isEditing,
    );
    const hasActiveSaving = Object.values(savingFields).some(
      (isSaving) => isSaving,
    );

    if (hasActiveEditing || hasActiveSaving) {
      return;
    }

    // Проверяем, есть ли пользовательские изменения в localValues
    const hasUserChanges =
      localValues.include_bonuses !==
        (projectDeliveryData.include_bonuses ?? true) ||
      localValues.quality_control_percent !==
        projectDeliveryData.quality_control_percent ||
      localValues.direct_bonus_percent !==
        projectDeliveryData.direct_bonus_percent ||
      localValues.average_salary !== projectDeliveryData.average_salary;

    if (hasUserChanges) {
      return;
    }

    const newValues = {
      include_bonuses: projectDeliveryData.include_bonuses ?? true,
      quality_control_percent: projectDeliveryData.quality_control_percent,
      direct_bonus_percent: projectDeliveryData.direct_bonus_percent,
      average_salary: projectDeliveryData.average_salary,
    };

    // Обновляем только при реальном изменении
    if (
      newValues.include_bonuses !== localValues.include_bonuses ||
      newValues.quality_control_percent !==
        localValues.quality_control_percent ||
      newValues.direct_bonus_percent !== localValues.direct_bonus_percent ||
      newValues.average_salary !== localValues.average_salary
    ) {
      setLocalValues(newValues);
    }
  }, [projectDeliveryData, editingFields, savingFields, localValues]);

  // Синхронизируем локальные изменения с контекстом в реальном времени
  useEffect(() => {
    // Обновляем контекст с текущими локальными значениями
    const currentEstimateData = estimateData || {};
    const updatedEstimateData = {
      ...currentEstimateData,
      project_delivery: {
        ...currentEstimateData.project_delivery,
        include_bonuses: localValues.include_bonuses,
        average_salary: localValues.average_salary,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
      },
    };

    // Проверяем, действительно ли нужно обновлять контекст
    const currentProjectDelivery = currentEstimateData.project_delivery || {};
    const needsUpdate =
      currentProjectDelivery.include_bonuses !== localValues.include_bonuses ||
      currentProjectDelivery.average_salary !== localValues.average_salary ||
      currentProjectDelivery.quality_control_percent !==
        localValues.quality_control_percent ||
      currentProjectDelivery.direct_bonus_percent !==
        localValues.direct_bonus_percent;

    if (needsUpdate) {
      setEstimateData(updatedEstimateData);
    }
    // estimateData намеренно исключён из зависимостей для предотвращения бесконечного цикла
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValues, setEstimateData]);

  // Определяем колонки на основе ролей из API
  const leadColumns = roles.map((role) => role.role) || [];

  // Получаем данные для таблицы из API
  const getTableData = () => {
    const laborData = roles.map((role) =>
      (role.day_efforts?.direct ?? 0).toFixed(1),
    );
    const bonusDaysData = roles.map((role) =>
      (role.day_efforts?.bonus ?? 0).toFixed(1),
    );
    const directCostsData = roles.map(
      (role) => `₸ ${(role.money_expenditures?.direct ?? 0).toLocaleString()}`,
    );
    const bonusMoneyData = roles.map(
      (role) => `₸ ${(role.money_expenditures?.bonus ?? 0).toLocaleString()}`,
    );
    return {
      labor: laborData,
      bonusDays: bonusDaysData,
      directCosts: directCostsData,
      bonusMoney: bonusMoneyData,
    };
  };

  const tableData = getTableData();

  // Функция для калькуляции данных ведения проекта (overrideEstimateData — при добавлении/удалении разделов)
  const calculateLeadData = async (updatedLeadData, overrideEstimateData) => {
    try {
      const currentEstimateData = overrideEstimateData || estimateData || {};
      const currentSectionsData = contextSections || {};

      const updatedEstimateData = {
        ...currentEstimateData,
        project_delivery: {
          ...currentEstimateData.project_delivery,
          include_bonuses: updatedLeadData.include_bonuses,
          average_salary: updatedLeadData.average_salary,
          quality_control_percent: updatedLeadData.quality_control_percent,
          direct_bonus_percent: updatedLeadData.direct_bonus_percent,
          roles: currentEstimateData.project_delivery?.roles ?? [],
        },
      };

      setEstimateData(updatedEstimateData);

      const calculationResult = await calculateConstructionDesign(
        estimateContext.projectId,
        updatedEstimateData,
        currentSectionsData,
      );

      if (estimateContext && estimateContext.updateHeaderData) {
        estimateContext.updateHeaderData({
          contract_value: calculationResult.contract_value,
          contract_value_before_nds:
            calculationResult.contract_value_before_nds,
          contract_value_construction:
            calculationResult.contract_value_construction,
          contract_value_construction_percent:
            calculationResult.contract_value_construction_percent,
          cost_price: calculationResult.cost_price,
          cost_price_percent: calculationResult.cost_price_percent,
          difference: calculationResult.difference,
          nds_value: calculationResult.nds_value,
          indirect_expenditures: calculationResult.indirect_expenditures,
          indirect_expenditures_percent:
            calculationResult.indirect_expenditures_percent,
          pure_income: calculationResult.pure_income,
          pure_income_percent: calculationResult.pure_income_percent,
        });
      }

      // Сохраняем наш список ролей, подставляем рассчитанные поля из ответа
      if (calculationResult && calculationResult.project_delivery) {
        const rolesWeSent = updatedEstimateData.project_delivery?.roles ?? [];
        const rolesFromApi = calculationResult.project_delivery.roles ?? [];
        const mergedRoles = rolesWeSent.map((ourRole) => {
          const fromApi = rolesFromApi.find((r) => r.role === ourRole.role);
          return fromApi ? { ...ourRole, ...fromApi } : ourRole;
        });
        const mergedProjectDelivery = {
          ...calculationResult.project_delivery,
          roles: mergedRoles,
        };
        updateEstimateSection("project_delivery", mergedProjectDelivery);
      }

      setLocalValues((prev) => ({
        ...prev,
        quality_control_percent:
          calculationResult.project_delivery?.quality_control_percent ??
          prev.quality_control_percent,
        direct_bonus_percent:
          calculationResult.project_delivery?.direct_bonus_percent ??
          prev.direct_bonus_percent,
        average_salary:
          calculationResult.project_delivery?.average_salary ??
          prev.average_salary,
      }));

      return calculationResult;
    } catch (error) {
      throw error;
    }
  };

  // Обработчик для изменения чекбокса "Учитывать бонусы при расчете"
  const handleCheckboxChange = async (fieldName, checked) => {
    try {
      setLocalValues((prev) => ({
        ...prev,
        [fieldName]: checked,
      }));

      // Вызываем калькуляцию с обновленными данными
      await calculateLeadData({
        ...localValues,
        [fieldName]: checked,
      });
    } catch (error) {
      throw error;
    }
  };

  // Обработчики для редактируемых полей
  const handleEditField = (fieldName, value) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: true }));
    setLocalValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleSaveField = async (fieldName, value) => {
    try {
      setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

      const parsedValue = parseFloat(value);

      // Обновляем локальные значения
      setLocalValues((prev) => ({
        ...prev,
        [fieldName]: parsedValue,
      }));

      // Вызываем калькуляцию с обновленными данными
      await calculateLeadData({
        ...localValues,
        [fieldName]: parsedValue,
      });

      setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    } catch (error) {
      throw error;
    } finally {
      setSavingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCancelEdit = (fieldName) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    setLocalValues((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "quality_control_percent"
          ? qualityControlPercent
          : fieldName === "direct_bonus_percent"
            ? directBonusPercent
            : fieldName === "average_salary"
              ? projectDeliveryData.average_salary
              : prev[fieldName], // Для остальных полей оставляем текущее значение
    }));
  };

  const handleAddLeadSection = () => {
    setSelectedLeadSections([]);
    setLeadSectionSearch("");
    setShowAddLeadSectionModal(true);
  };

  const handleCancelAddLeadSection = () => {
    setShowAddLeadSectionModal(false);
    setSelectedLeadSections([]);
    setLeadSectionSearch("");
  };

  const handleConfirmAddLeadSection = async () => {
    if (selectedLeadSections.length === 0) return;
    const toAdd = workTabSections.filter((s) =>
      selectedLeadSections.includes(s.id),
    );
    if (toAdd.length === 0) return;

    const newRoleObjects = toAdd.map((s) => defaultLeadRole(s.name));
    const newRoles = [...displayRoles, ...newRoleObjects];

    const newEstimateData = {
      ...estimateData,
      project_delivery: {
        ...estimateData?.project_delivery,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);

    try {
      await calculateLeadData(localValues, newEstimateData);
    } catch (e) {
      console.error(e);
    }

    setShowAddLeadSectionModal(false);
    setSelectedLeadSections([]);
    setLeadSectionSearch("");
  };

  const handleRemoveLeadColumn = async (roleIndex) => {
    const newRoles = displayRoles.filter((_, i) => i !== roleIndex);
    const newEstimateData = {
      ...estimateData,
      project_delivery: {
        ...estimateData?.project_delivery,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);
    try {
      await calculateLeadData(localValues, newEstimateData);
    } catch (e) {
      console.error(e);
    }
  };

  const [leadDragOverCol, setLeadDragOverCol] = useState(null);

  const reorderArray = (arr, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return arr;
    const list = [...arr];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    return list;
  };

  const handleReorderLeadColumns = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newRoles = reorderArray(displayRoles, fromIndex, toIndex);
    const newEstimateData = {
      ...estimateData,
      project_delivery: {
        ...estimateData?.project_delivery,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);
    try {
      await calculateLeadData(localValues, newEstimateData);
    } catch (e) {
      console.error(e);
    }
  };

  // Компонент для редактируемого поля
  const EditableField = ({
    fieldName,
    value,
    isCurrency = false,
    isPercent = false,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const inputRef = React.useRef(null);

    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : value;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step={isPercent ? "0.01" : "1"}
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(fieldName, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(value);
                onCancel(fieldName);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              onSave(fieldName, localValue);
            }}
            disabled={!isEditable || isSaving}
          />
        </div>
      );
    }

    const displayValue = isCurrency
      ? `₸ ${Number(value || 0).toLocaleString()}`
      : isPercent
        ? `${value}`
        : value;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
      >
        <span
          style={{
            fontSize: "16px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditable ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !isEditable ? "default" : "pointer",
          }}
          onClick={() => (!isEditable ? undefined : onEdit(fieldName, value))}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  const leadTable = [
    {
      label: "Трудозатраты (дн)",
      editable: false,
      values: tableData.labor,
    },
    {
      label: "Бонусы в днях (дн)",
      values: tableData.bonusDays,
    },
    {
      label: "Прямые затраты (тг)",
      values: tableData.directCosts,
    },
    {
      label: "Бонусы в деньгах (тг)",
      values: tableData.bonusMoney,
    },
  ];

  // Рассчитываем общую сумму из данных API
  const totalDirect = projectDeliveryData.total_money_expenditures?.direct;
  const totalBonus = projectDeliveryData.total_money_expenditures?.bonus;
  const total = projectDeliveryData.total || 0;

  // Экспортируем функции через ref
  useImperativeHandle(ref, () => ({
    getTabData: () => {
      return {
        project_delivery: {
          include_bonuses: localValues.include_bonuses,
          quality_control_percent: localValues.quality_control_percent,
          direct_bonus_percent: localValues.direct_bonus_percent,
          average_salary: localValues.average_salary,
        },
      };
    },
    triggerCalculation: async () => {
      try {
        // Вызываем калькуляцию с текущими локальными значениями
        const calculationResult = await calculateLeadData(localValues);
        return calculationResult;
      } catch (error) {
        throw error;
      }
    },
    handleSaveForEstimate: async () => {
      try {
        // Используем функцию сохранения из контекста
        const saveResult = await performSave(estimateContext.projectId);

        return saveResult;
      } catch (error) {
        throw error;
      }
    },
  }));

  return (
    <div className="lead-table-block">
      {/* Чекбокс "Учитывать бонусы при расчете" и кнопка "Добавить раздел" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Учитывать бонусы при расчете:
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={localValues.include_bonuses}
              onChange={(e) =>
                handleCheckboxChange("include_bonuses", e.target.checked)
              }
              disabled={!isEditable}
            />
            <span className="slider"></span>
          </label>
        </div>
        <button
          onClick={handleAddLeadSection}
          disabled={!isEditable}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            backgroundColor: "#fff",
            color: "#2A5E38",
            border: "none",
            borderRadius: "4px",
            cursor: !isEditable ? "not-allowed" : "pointer",
            opacity: !isEditable ? 0.6 : 1,
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
          Добавить раздел
        </button>
      </div>

      {/* Редактируемые поля в одной строке */}
      <div className="lead-title-row">
        <div className="lead-title-block">
          <div className="lead-title">% доля ведения проекта</div>
          <EditableField
            fieldName="quality_control_percent"
            value={localValues.quality_control_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.quality_control_percent}
            onEdit={handleEditField}
            isSaving={savingFields.quality_control_percent}
          />
        </div>
        <div className="lead-title-block">
          <div className="lead-title">% доля бонусов от ПЗ</div>
          <EditableField
            fieldName="direct_bonus_percent"
            value={localValues.direct_bonus_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.direct_bonus_percent}
            onEdit={handleEditField}
            isSaving={savingFields.direct_bonus_percent}
          />
        </div>
        <div className="lead-title-block">
          <div className="lead-title">Средняя ставка</div>
          <EditableField
            fieldName="average_salary"
            value={localValues.average_salary}
            isCurrency={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.average_salary}
            onEdit={handleEditField}
            isSaving={savingFields.average_salary}
          />
        </div>
      </div>

      <div className="lead-table-mock">
        {leadColumns.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th></th>
                {leadColumns.map((col, i) => (
                  <th
                    key={i}
                    draggable={isEditable}
                    onDragStart={(e) => {
                      if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(i));
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
                      setLeadDragOverCol(i);
                    }}
                    onDragLeave={() => setLeadDragOverCol(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setLeadDragOverCol(null);
                      const fromStr = e.dataTransfer?.getData("text/plain");
                      if (fromStr == null) return;
                      const fromIndex = parseInt(fromStr, 10);
                      if (Number.isNaN(fromIndex) || fromIndex === i) return;
                      handleReorderLeadColumns(fromIndex, i);
                    }}
                    onDragEnd={() => setLeadDragOverCol(null)}
                    style={{
                      cursor: isEditable ? "grab" : "default",
                      backgroundColor: leadDragOverCol === i ? "rgba(42, 94, 56, 0.12)" : undefined,
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {isEditable && (
                        <span
                          title="Перетащите для изменения порядка"
                          style={{
                            cursor: "grab",
                            color: "#888",
                            fontSize: "14px",
                            userSelect: "none",
                          }}
                          onMouseDown={(ev) => ev.stopPropagation()}
                        >
                          ⋮⋮
                        </span>
                      )}
                      <span>{col}</span>
                      {isEditable && (
                        <button
                          type="button"
                          onClick={() => handleRemoveLeadColumn(i)}
                          title="Удалить раздел"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            background: "none",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            color: "#dc3545",
                            fontSize: "18px",
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leadTable.map((row, ridx) => (
                <tr key={ridx}>
                  <td style={{ fontWeight: "500" }}>{row.label}</td>
                  {row.values.map((val, i) => (
                    <td key={i} style={{ textAlign: "center" }}>
                      {val}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontSize: "16px",
            }}
          >
            Нет разделов. Добавьте разделы с помощью кнопки «Добавить раздел».
          </div>
        )}
      </div>

      {/* Модалка: только разделы из Рабочего проектирования */}
      {showAddLeadSectionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCancelAddLeadSection}
        >
          <div
            className="add-section-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              minWidth: "400px",
              maxWidth: "500px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 20px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Добавление раздела в Ведение проекта
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Доступны только разделы, добавленные на вкладке «Рабочее
              проектирование».
            </p>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Выберите раздел из списка*
              </label>
              <input
                type="text"
                placeholder="Поиск"
                value={leadSectionSearch}
                onChange={(e) => setLeadSectionSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  marginBottom: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{
                  maxHeight: "240px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                {workTabSections.length === 0 ? (
                  <div
                    style={{
                      padding: "16px",
                      color: "#666",
                      fontSize: "14px",
                    }}
                  >
                    Нет разделов с вкладки «Рабочее проектирование». Добавьте
                    разделы там сначала.
                  </div>
                ) : (
                  (() => {
                    const searchLower = (leadSectionSearch || "")
                      .toLowerCase()
                      .trim();
                    const filtered = searchLower
                      ? workTabSections.filter((s) =>
                          (s.name || "").toLowerCase().includes(searchLower),
                        )
                      : workTabSections;
                    const alreadyInLead = new Set(
                      displayRoles.map((r) => r.role),
                    );
                    const available = filtered.filter(
                      (s) => !alreadyInLead.has(s.name),
                    );
                    if (available.length === 0) {
                      return (
                        <div
                          style={{
                            padding: "16px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          {filtered.length === 0
                            ? "Ничего не найдено"
                            : "Все эти разделы уже добавлены."}
                        </div>
                      );
                    }
                    return available.map((section) => {
                      const checked = selectedLeadSections.includes(section.id);
                      return (
                        <label
                          key={section.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "8px 6px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedLeadSections((prev) =>
                                e.target.checked
                                  ? [...prev, section.id]
                                  : prev.filter((x) => x !== section.id),
                              );
                            }}
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          <span className="section-label-text">
                            <span className="section-label-name">{section.name}</span>
                            {section.description && (
                              <span className="section-label-description">
                                {section.description}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    });
                  })()
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={handleCancelAddLeadSection}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmAddLeadSection}
                disabled={selectedLeadSections.length === 0}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedLeadSections.length > 0 ? "#2A5E38" : "#ccc",
                  color: "white",
                  cursor:
                    selectedLeadSections.length > 0 ? "pointer" : "not-allowed",
                  fontSize: "14px",
                  opacity: selectedLeadSections.length > 0 ? 1 : 0.6,
                }}
              >
                Добавить
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="lead-table-footer">
        <div className="lead-footer-main">
          <div className="lead-footer-label">ВСЕ ЗАТРАТЫ</div>
          <div className="lead-footer-sum main">₸ {total.toLocaleString()}</div>
        </div>
        <div className="lead-footer-right">
          <div className="lead-footer-block">
            <div className="lead-footer-label">Прямые затраты</div>
            <div className="lead-footer-sum">
              ₸ {totalDirect.toLocaleString()}
            </div>
          </div>
          <div className="lead-footer-block">
            <div className="lead-footer-label">Бонусы</div>
            <div className="lead-footer-sum">
              ₸ {totalBonus.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default LeadTab;
